import type { PortalDealRow } from "@colab/shared";

// Per-currency formatter mirroring the legacy /app/portal/deals/page.tsx
// `formatAmount` helper. en-IE locale, 0 decimal places, Intl-throws-on-
// bad-code fallback.
//
// Quality bar: currency tags are silent killers. The list aggregates and
// per-deal display both flow through here; never silently coerce to EUR
// unless the row says EUR.
export function formatCurrency(
  value: number | null | undefined,
  currency: string,
): string {
  if (value == null) return "—";
  try {
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toLocaleString("en-IE", { maximumFractionDigits: 0 })} ${currency}`;
  }
}

// Aggregates a list of rows into per-currency totals, then renders them as
// "€500K · $300K" style (joined with ' · '). Returns null if there are no
// non-zero totals — the caller can hide the aggregate row in that case.
//
// Inbound vs outbound separation is the bucket's signature constraint
// (plan quality bar). Callers MUST filter to one direction before passing
// rows in — never blend.
export function formatPerCurrencyTotals(rows: PortalDealRow[]): string | null {
  const byCurrency = new Map<string, number>();
  for (const r of rows) {
    if (r.amount == null) continue;
    const code = r.currency_code || "EUR";
    byCurrency.set(code, (byCurrency.get(code) ?? 0) + r.amount);
  }
  const entries = [...byCurrency.entries()].filter(([, v]) => v > 0);
  if (entries.length === 0) return null;
  return entries.map(([code, v]) => formatCurrency(v, code)).join(" · ");
}
